/**
 * CategoryFeed (#1617, redesigned #2114, single period column #2254) — the
 * category-grouped budget view. A 3-level accordion under a period-saldo HERO:
 *   L1 ``LedgerGroup`` header (collapsible; signed netted Plan/Ist figures + an
 *      Abweichung chip — mixed-direction groups net into a saldo, no special case) →
 *   L2 ``ledger_category`` row (the "item": label + ONE period column — the
 *      selected month, or the selected Wirtschaftsjahr — a ``CellFigures`` unit:
 *      Plan | Ist | chip) →
 *   the unfolded category's transactions for the period (a thin per-tx detail).
 *
 * The selected ``period`` (#2254) picks which window the single column renders:
 * a month selection reads the ``planMonth``/``istMonth`` pair, a Wirtschaftsjahr
 * selection the ``planYear``/``istYear`` pair the rollup already computes. The
 * old parallel month|WJ column pair is gone.
 *
 * The Abweichung chip is the ONLY coloured element (green = besser als geplant,
 * red = schlechter, neutral "wie geplant" band) — no bars, no percentages. The
 * saldo hero keeps the #1712 "Saldo des Zeitraums ≠ Kontostand" disambiguation.
 *
 * Reads ``rollupByCategory`` (pure) — this is a READ-ONLY lens over the SAME records
 * the ledger reads; it never edits and never touches the projection/verdict/series.
 * One category open at a time (mirrors LedgerFeed's accordion).
 */
import { useMemo } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { EntityTile } from '@hofsync/design-system';
import DirectionIcon from './DirectionIcon';
import { formatEur } from '../../../utils/formatEur';
import { periodHasPlan, recordInWjWindow, rollupByCategory, unsortedRollup, type CategoryRollup, type GroupRollup } from './categoryRollup';
import type { LedgerDirection } from './ledgerCategory';
import { isFullyPostHorizon } from './monthlyColumns';
import { periodAnchorYm, type SelectedPeriod } from './selectedPeriod';
import { headRecord, signedTotal } from './transactions';
import type { LiquiditaetRecord, Transaction } from './types';
import './CategoryFeed.css';

const formatDay = (iso: string): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** income → +, expense → − (mixed groups sum their single-direction categories). */
const signOf = (d: LedgerDirection): number => (d === 'out' ? -1 : d === 'in' ? 1 : 0);

/** A record's effective period ('YYYY-MM'): ``period_ym`` else the value-date month. */
const recordMonth = (r: LiquiditaetRecord): string => r.period_ym || r.value_date.slice(0, 7);

/** Signed Plan/Ist for one window (month/year) of one category. A single-direction
 *  category signs the rollup magnitude; a BOTH-direction category (e.g. ``priv_share``)
 *  nets its own records' directions instead — ``signOf('both')`` is 0, so multiplying
 *  the magnitude would zero real amounts (codex #2114). */
function signedCat(
  cat: CategoryRollup,
  field: 'planMonth' | 'istMonth' | 'planYear' | 'istYear',
  selectedYm: string,
  wjStartMonth: number,
): number {
  if (cat.direction !== 'both') return signOf(cat.direction) * cat[field];
  const role = field === 'planMonth' || field === 'planYear' ? 'planned' : 'payed';
  const inWindow = (r: LiquiditaetRecord): boolean =>
    field === 'planMonth' || field === 'istMonth'
      ? recordMonth(r) === selectedYm
      : recordInWjWindow(r, selectedYm, wjStartMonth);
  return cat.txns.reduce((sum, tx) => {
    const r = tx[role];
    return r && inWindow(r) ? sum + signedTotal(r) : sum;
  }, 0);
}

/** The record a transaction is dated by: its in-WJ leg (payed first, then planned),
 *  else the head record. Single source of truth for BOTH the line's displayed date and
 *  the list sort, so the two never disagree. */
function effectiveTxnRecord(tx: Transaction, selectedYm: string, wjStartMonth: number): LiquiditaetRecord {
  const payedInWj = !!tx.payed && recordInWjWindow(tx.payed, selectedYm, wjStartMonth);
  const plannedInWj = !!tx.planned && recordInWjWindow(tx.planned, selectedYm, wjStartMonth);
  return (payedInWj && tx.payed) || (plannedInWj && tx.planned) || headRecord(tx);
}

/** The Abweichung chip — the ONLY coloured element in a cell. Compact by default
 *  (``▲ 300,00 €``) with the full direction phrase ("… besser als geplant") in
 *  ``title`` + ``aria-label``; the saldo hero (``full``) renders the phrase inline.
 *  The neutral band renders its "wie geplant" text as-is.
 *
 *  Precedence in the ONE chip (the future-month case is handled a level up — #2269
 *  suppresses the chip entirely — so the order at render time is future → Kein Plan →
 *  variance): when ``hasPlan === false`` the period has NO planned records at all, so
 *  ``saldoVariance(0, ist)`` would fire a meaningless full-magnitude chip (#2270).
 *  Short-circuit to a neutral grey "Kein Plan" badge. The signal is record-presence,
 *  NOT ``plan === 0`` — a real net-zero plan keeps its variance chip. */
function VarianceChip({ plan, ist, hasPlan, full = false }: { plan: number; ist: number; hasPlan: boolean; full?: boolean }) {
  if (!hasPlan) {
    const noPlanPhrase = 'Kein Plan für diesen Zeitraum';
    return (
      <span
        className="liquiditaetsplanung-catfeed__variance-chip liquiditaetsplanung-catfeed__variance-chip--noplan"
        title={noPlanPhrase}
        aria-label={noPlanPhrase}
      >
        Kein Plan
      </span>
    );
  }
  const v = saldoVariance(plan, ist);
  const phrase = v.tone === 'neutral' ? v.word : `${v.arrow} ${formatEur(v.amount)} ${v.word}`;
  const text = v.tone === 'neutral' || full ? phrase : `${v.arrow} ${formatEur(v.amount)}`;
  return (
    <span
      className={`liquiditaetsplanung-catfeed__variance-chip liquiditaetsplanung-catfeed__variance-chip--${v.tone}`}
      title={phrase}
      aria-label={phrase}
    >
      {text}
    </span>
  );
}

/** One window cell (selected month, or the Wirtschaftsjahr) of an L1/L2 row: a signed
 *  Plan figure + a signed Ist figure (income +, expense −; aligned over the unfolded
 *  transaction chips) and the Abweichung chip beneath. ``active=false`` — no plan AND
 *  no ist in this window — renders an EMPTY cell that keeps its width, never a
 *  spurious "wie geplant" chip from ``saldoVariance(0, 0)``. ``hideVariance`` suppresses
 *  the chip only (the figures stay) — a strictly-future month has no Ist by construction,
 *  so ``saldoVariance(plan, 0)`` would fire a meaningless full-magnitude chip (#2269). */
function CellFigures({
  plan,
  ist,
  active,
  hideVariance,
  hideIst,
  hasPlan,
}: {
  plan: number;
  ist: number;
  active: boolean;
  hideVariance: boolean;
  /** #3158 — fully-post-horizon month: the Ist figure is structurally 0, so the
   *  Ist half renders as an EMPTY placeholder (keeps the Plan column on its
   *  6.25rem grid slot under the subhead) and the aria-label drops the figure —
   *  a hidden value must not stay announced. */
  hideIst: boolean;
  /** Whether the window has ANY planned record (#2270). false ⇒ the chip renders a
   *  grey "Kein Plan" badge instead of a full-magnitude variance against a zero Plan. */
  hasPlan: boolean;
}) {
  if (!active) {
    return (
      <span className="liquiditaetsplanung-catfeed__column liquiditaetsplanung-catfeed__cell" aria-hidden="true" />
    );
  }
  // + 0 normalises the signed zero: ``signOf('out') * 0`` is JS −0, which Intl
  // would render as "-0,00 €".
  const planText = formatEur(plan + 0);
  const istText = formatEur(ist + 0);
  return (
    <span className="liquiditaetsplanung-catfeed__column liquiditaetsplanung-catfeed__cell">
      {/* The visual Plan/Ist subhead is aria-hidden, so the figure pair names itself
          for assistive tech — the same role="img" + aria-label pattern the deleted
          ConsumptionBar used (codex #2114). */}
      <span
        className="liquiditaetsplanung-catfeed__cell-figs"
        role="img"
        aria-label={hideIst ? `Plan ${planText}` : `Plan ${planText}, Ist ${istText}`}
      >
        <span className="liquiditaetsplanung-catfeed__cell-plan" aria-hidden="true">{planText}</span>
        <span className="liquiditaetsplanung-catfeed__cell-ist" aria-hidden="true">
          {hideIst ? null : istText}
        </span>
      </span>
      {!hideVariance && <VarianceChip plan={plan} ist={ist} hasPlan={hasPlan} />}
    </span>
  );
}

/** The transaction line's single period figure column (#2254): a plan subcolumn (the
 *  planned amount in a grey placeholder chip) + an Ist subcolumn (the actual, to its
 *  right). A column with no plan AND no Ist in the window renders empty but keeps its
 *  width, so the lines stay aligned under the period header. An active column with no
 *  plan shows a faint empty chip (the plan slot, unfilled). */
function TxnFigCol({ plan, ist }: { plan: number | null; ist: number | null }) {
  if (plan == null && ist == null) {
    return <span className="liquiditaetsplanung-catfeed__txn-colgroup" aria-hidden="true" />;
  }
  return (
    <span className="liquiditaetsplanung-catfeed__txn-colgroup">
      <span className="liquiditaetsplanung-catfeed__txn-plan">
        {plan != null ? (
          <span className="liquiditaetsplanung-catfeed__txn-plan-chip">{formatEur(plan)}</span>
        ) : (
          <span
            className="liquiditaetsplanung-catfeed__txn-plan-chip liquiditaetsplanung-catfeed__txn-plan-chip--empty"
            aria-hidden="true"
          />
        )}
      </span>
      <span className="liquiditaetsplanung-catfeed__txn-ist">{ist != null ? formatEur(ist) : null}</span>
    </span>
  );
}

/** A single transaction line inside an unfolded category: date · label · ONE period
 *  figure column (#2254 — the selected month, or the selected Wirtschaftsjahr), split
 *  into a plan (grey chip) and an Ist subcolumn — aligned under the period header
 *  exactly like the category rows. Legs are signed (income +, expense −). The
 *  "Plan"/"Ist" labels and the old "→" arrow are gone — grey chip = plan, figure to
 *  its right = Ist. */
function TxnLine({
  tx,
  anchorYm,
  wjStartMonth,
  yearScope,
  onOpen,
}: {
  tx: Transaction;
  anchorYm: string;
  wjStartMonth: number;
  /** Year (Wirtschaftsjahr) selection ⇒ the WJ window; month selection ⇒ the month. */
  yearScope: boolean;
  /** Click the line → open the transaction form modal (same as the Buchungen view). */
  onOpen?: (txId: string) => void;
}) {
  const inWj = (r: Transaction['planned']): boolean => !!r && recordInWjWindow(r, anchorYm, wjStartMonth);
  const inMonth = (r: Transaction['planned']): boolean => !!r && recordMonth(r) === anchorYm;
  const inWindow = yearScope ? inWj : inMonth;
  const plan = inWindow(tx.planned) ? signedTotal(tx.planned!) : null;
  const ist = inWindow(tx.payed) ? signedTotal(tx.payed!) : null;
  // Same record (and therefore date) the list is sorted by — see effectiveTxnRecord.
  const dateRecord = effectiveTxnRecord(tx, anchorYm, wjStartMonth);
  return (
    <button type="button" className="liquiditaetsplanung-catfeed__txn" onClick={() => onOpen?.(tx.id)}>
      <span className="liquiditaetsplanung-catfeed__txn-date">{formatDay(dateRecord.value_date)}</span>
      <span className="liquiditaetsplanung-catfeed__txn-label">{tx.label}</span>
      <span className="liquiditaetsplanung-catfeed__txn-figs">
        <TxnFigCol plan={plan} ist={ist} />
      </span>
    </button>
  );
}

/** One L2 category row: a SINGLE period CellFigures cell (#2254 — the month pair,
 *  or the Wirtschaftsjahr pair the rollup already computes). */
function CategoryRow({
  cat,
  anchorYm,
  wjStartMonth,
  yearScope,
  hideVariance,
  hideIst,
  isOpen,
  onToggle,
  onTransactionClick,
}: {
  cat: CategoryRollup;
  anchorYm: string;
  wjStartMonth: number;
  yearScope: boolean;
  /** Strictly-future selected month (#2269) — suppress the row's Abweichung chip. */
  hideVariance: boolean;
  /** Fully-post-horizon selected month (#3158) — drop the row's Ist figure too. */
  hideIst: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onTransactionClick?: (txId: string) => void;
}) {
  const income = cat.direction === 'in';
  // The both-direction Layers branch survives the icon swap (#2254) — only the
  // single-direction glyphs become the branded wallet icons.
  const icon = cat.direction === 'both'
    ? <Layers size={16} />
    : <DirectionIcon income={income} />;
  // Only transactions with a leg in the SELECTED window list as lines (#2254):
  // with the second column gone, an out-of-window line would render date+label
  // with no figures at all — a phantom booking (review r1). In year scope the
  // rollup already pushes only in-WJ contributors, so the filter only prunes in
  // month scope. Chronological order (oldest → newest) by each line's displayed
  // date (the rollup pushes in record-grouping order, not by date). Computed
  // only while unfolded — closed rows skip the filter+sort entirely.
  const lineInWindow = (r: Transaction['planned']): boolean =>
    !!r && (yearScope ? recordInWjWindow(r, anchorYm, wjStartMonth) : recordMonth(r) === anchorYm);
  const sortedTxns = isOpen
    ? cat.txns
        .filter((tx) => lineInWindow(tx.planned) || lineInWindow(tx.payed))
        .map((tx) => ({ tx, date: effectiveTxnRecord(tx, anchorYm, wjStartMonth).value_date }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((e) => e.tx)
    : [];
  return (
    <div className="liquiditaetsplanung-catfeed__category">
      <EntityTile
        expandable
        isExpanded={isOpen}
        onToggle={onToggle}
        icon={icon}
        iconVariant={income ? 'success' : 'warning'}
        title={cat.label}
        tags={
          <span className="liquiditaetsplanung-catfeed__columns">
            <CellFigures
              plan={signedCat(cat, yearScope ? 'planYear' : 'planMonth', anchorYm, wjStartMonth)}
              ist={signedCat(cat, yearScope ? 'istYear' : 'istMonth', anchorYm, wjStartMonth)}
              active={
                yearScope
                  ? cat.planYear > 0 || cat.istYear > 0
                  : cat.planMonth > 0 || cat.istMonth > 0
              }
              hideVariance={hideVariance}
              hideIst={hideIst}
              hasPlan={yearScope ? cat.hasPlanYear : cat.hasPlanMonth}
            />
          </span>
        }
      >
        {/* Render the (potentially long) transaction list only while unfolded —
            EntityTile keeps its body mounted, so a guard here both keeps the DOM
            lean and makes the accordion's open/closed state observable. */}
        {isOpen && (
          <div className="liquiditaetsplanung-catfeed__txns">
            {sortedTxns.length === 0 ? (
              <p className="liquiditaetsplanung-catfeed__txns-empty">Keine Buchungen im Zeitraum.</p>
            ) : (
              sortedTxns.map((tx) => (
                <TxnLine
                  key={tx.id}
                  tx={tx}
                  anchorYm={anchorYm}
                  wjStartMonth={wjStartMonth}
                  yearScope={yearScope}
                  onOpen={onTransactionClick}
                />
              ))
            )}
          </div>
        )}
      </EntityTile>
    </div>
  );
}

/** The L1 group header: a collapse toggle + a single-period CellFigures rollup (#2254). */
function GroupHeader({
  group,
  anchorYm,
  wjStartMonth,
  yearScope,
  hideVariance,
  hideIst,
  collapsed,
  onToggle,
}: {
  group: GroupRollup;
  anchorYm: string;
  wjStartMonth: number;
  yearScope: boolean;
  /** Strictly-future selected month (#2269) — suppress the group's Abweichung chip. */
  hideVariance: boolean;
  /** Fully-post-horizon selected month (#3158) — drop the group's Ist figure too. */
  hideIst: boolean;
  collapsed: boolean;
  onToggle: () => void;
}) {
  // Signed Plan/Ist (income +, expense −) summed across the group's categories, for the
  // selected period. For a single-direction group this just signs the magnitudes; for a
  // mixed group (Finanzierung, Privat) it nets in- and out-flows into a SALDO — no
  // "gemischt" special case (#2114): the netted signed figures + the Abweichung chip
  // carry the direction on their own. ``signedCat`` also nets a both-direction CATEGORY
  // (priv_share) from its records instead of zeroing it.
  const signed = (field: 'planMonth' | 'istMonth' | 'planYear' | 'istYear'): number =>
    group.categories.reduce((s, c) => s + signedCat(c, field, anchorYm, wjStartMonth), 0);

  // The single rollup column (month or Wirtschaftsjahr — #2254). Activity is gated on
  // the MAGNITUDES (the same rule as the L2 cells), so a mixed group that happens to
  // net to zero still shows its figures rather than a blank cell.
  const plan = yearScope ? group.planYear : group.planMonth;
  const ist = yearScope ? group.istYear : group.istMonth;

  return (
    <button
      type="button"
      className="liquiditaetsplanung-catfeed__group-header"
      aria-expanded={!collapsed}
      onClick={onToggle}
    >
      <span className="liquiditaetsplanung-catfeed__group-title">
        {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        {group.label}
      </span>
      {/* The single aggregated period column, aligned under the period header exactly
          like the L2 category rows below. */}
      <span className="liquiditaetsplanung-catfeed__group-rollup">
        <CellFigures
          plan={signed(yearScope ? 'planYear' : 'planMonth')}
          ist={signed(yearScope ? 'istYear' : 'istMonth')}
          active={plan > 0 || ist > 0}
          hideVariance={hideVariance}
          hideIst={hideIst}
          hasPlan={yearScope ? group.hasPlanYear : group.hasPlanMonth}
        />
      </span>
    </button>
  );
}

/** The period-saldo variance tone, with direction = good/bad (NOT sign-agnostic — so this
 *  is deliberately NOT ``ConsumptionBar.toneOf``). */
export type SaldoVarianceTone = 'pos' | 'neg' | 'neutral';

export interface SaldoVariance {
  tone: SaldoVarianceTone;
  /** ``Math.abs(ist − plan)`` — rendered via ``formatEur`` so the chip is always a
   *  positive amount + a direction word, never a bare signed number. */
  amount: number;
  /** The direction phrase: "besser als geplant" / "schlechter als geplant" / "wie geplant". */
  word: string;
  /** "▲" (better) / "▼" (worse) / "" (on plan). */
  arrow: string;
}

/** Below this |Plan| the relative (%) band is skipped — a Ist/Plan ratio near a zero
 *  Plan explodes (the year Plan can be 111,16 € → ~9.900 %), so it can never gate the tone. */
const SALDO_PLAN_FLOOR_EUR = 100;
/** Within ±2 % of a (non-trivial) Plan counts as "wie geplant". */
const SALDO_REL_BAND = 0.02;

/** SALDO variance for one column — ``Abweichung = Ist − Plan``. For a net cash saldo,
 *  MORE cash than planned is *better*, so the tone follows the delta's sign (good/bad),
 *  never the raw Plan sign (the old bug that painted an on-track burn month red).
 *  Neutral ("wie geplant") inside a band: a to-the-cent absolute match
 *  (``Math.round(delta*100)===0`` — catches an exactly-on-plan column, including a
 *  negative net, which the old sign-colouring wrongly reddened), plus a ±2 % relative
 *  band gated behind a |Plan| floor. Sanity near a zero Plan comes from never surfacing a
 *  percentage (a Ist/Plan ratio explodes there) — NOT from a wide € cushion, so a real
 *  cent-level delta on a tiny Plan is still shown (correctly) as a small better/worse. */
export function saldoVariance(plan: number, ist: number): SaldoVariance {
  const delta = ist - plan;
  const amount = Math.abs(delta);
  const withinAbs = Math.round(delta * 100) === 0;
  const withinRel = Math.abs(plan) >= SALDO_PLAN_FLOOR_EUR && amount / Math.abs(plan) <= SALDO_REL_BAND;
  if (withinAbs || withinRel) {
    return { tone: 'neutral', amount, word: 'wie geplant', arrow: '' };
  }
  if (delta > 0) {
    return { tone: 'pos', amount, word: 'besser als geplant', arrow: '▲' };
  }
  return { tone: 'neg', amount, word: 'schlechter als geplant', arrow: '▼' };
}

/** The "Unsortiert" row (#2347) — rendered AFTER the four L1 groups, it surfaces the
 *  uncategorised (``unsorted``) bookings the budget lens drops (76 % of WF's records)
 *  so the hero saldo reconciles with the balance chart. It carries the net Ist on the
 *  shared column rail (no Plan — uncategorised bookings are unplannable, so it reuses
 *  the #2270 "Kein Plan" chip path), a muted/warning treatment, and a secondary line
 *  with the count + gross. Clicking it deep-links into the Buchungen ledger's
 *  categorise flow (``onShow`` → the page's ``handleViewChange('buchungen')``), exactly
 *  like the header ⚠ chip. Categorised crosscutting cash (``vat``, ``transfer_internal``)
 *  folds into the HERO net but never onto this row: every figure here — ``ist``,
 *  ``count``, ``gross`` — describes the still-uncategorised bookings alone, so the
 *  number matches the caption (#2679; an unpaired 400.000 € transfer leg otherwise
 *  showed up as "2 Buchungen noch nicht zugeordnet · 800,00 €" beside −400.200,00 €). */
function UnsortedRow({
  ist,
  count,
  gross,
  onShow,
}: {
  ist: number;
  count: number;
  gross: number;
  onShow?: () => void;
}) {
  const sub = `${count} ${count === 1 ? 'Buchung' : 'Buchungen'} noch nicht zugeordnet · ${formatEur(gross)}`;
  return (
    <button
      type="button"
      className="liquiditaetsplanung-catfeed__unsorted"
      onClick={onShow}
      title="Noch nicht kategorisierte Buchungen ansehen und zuordnen"
    >
      <span className="liquiditaetsplanung-catfeed__unsorted-heading">
        <span className="liquiditaetsplanung-catfeed__unsorted-title">
          <AlertTriangle size={16} aria-hidden="true" />
          Unsortiert
        </span>
        <span className="liquiditaetsplanung-catfeed__unsorted-sub">{sub}</span>
      </span>
      <span className="liquiditaetsplanung-catfeed__group-rollup">
        <span className="liquiditaetsplanung-catfeed__column liquiditaetsplanung-catfeed__cell">
          <span
            className="liquiditaetsplanung-catfeed__cell-figs"
            role="img"
            aria-label={`Ist ${formatEur(ist)}`}
          >
            {/* No Plan slot for an unplannable bucket — the empty chip keeps the Ist
                aligned under the "Ist" sub-label, mirroring an unplanned category row. */}
            <span
              className="liquiditaetsplanung-catfeed__cell-plan liquiditaetsplanung-catfeed__cell-plan--empty"
              aria-hidden="true"
            />
            <span className="liquiditaetsplanung-catfeed__cell-ist">{formatEur(ist + 0)}</span>
          </span>
          <VarianceChip plan={0} ist={ist} hasPlan={false} />
        </span>
      </span>
    </button>
  );
}

/** The single hero column of the period saldo (#1712 "Vorschlag A", repositioned to
 *  the feed top by #2114, one column per #2254 — the selected month OR the selected
 *  Wirtschaftsjahr): the net "Ist bisher" as the hero figure, the Plan beneath in a
 *  neutral colour, then the Abweichung chip — the only coloured element — with the
 *  full direction phrase (``saldoVariance``). "Ist bisher" (not bare "Ist") flags
 *  that the actuals are period-to-date against a whole-period Plan forecast.
 *  ``hideVariance`` suppresses the hero chip for a strictly-future month (#2269) —
 *  Ist is structurally 0 there, so the variance would be a meaningless full-Plan chip.
 *  ``hideIst`` (#3158) goes further for a fully-POST-HORIZON month: the "0,00 €"
 *  hero figure and its "Ist bisher" label are dropped entirely (the Plan line
 *  stays as the cell's content) — a month with no actuals by construction must
 *  not present a confident zero. */
function SaldoCell({ plan, ist, hideVariance, hideIst, hasPlan }: { plan: number; ist: number; hideVariance: boolean; hideIst: boolean; hasPlan: boolean }) {
  return (
    <span className="liquiditaetsplanung-catfeed__column liquiditaetsplanung-catfeed__saldo-cell">
      {!hideIst && <span className="liquiditaetsplanung-catfeed__saldo-ist">{formatEur(ist)}</span>}
      {!hideIst && <span className="liquiditaetsplanung-catfeed__saldo-istlabel">Ist bisher</span>}
      <span className="liquiditaetsplanung-catfeed__saldo-plan">
        <span className="liquiditaetsplanung-catfeed__saldo-planlabel">Plan</span> {formatEur(plan)}
      </span>
      {!hideVariance && <VarianceChip plan={plan} ist={ist} hasPlan={hasPlan} full />}
    </span>
  );
}

export default function CategoryFeed({
  records,
  period,
  wjStartMonth,
  todayYm,
  istHorizon,
  expandedGroups,
  onToggleGroup,
  openCategoryId,
  onToggleCategory,
  onTransactionClick,
  onShowUnsorted,
}: {
  records: LiquiditaetRecord[];
  /** The selected period (#2254): a month, or a whole Wirtschaftsjahr — picks
   *  which window pair the single figure column renders. */
  period: SelectedPeriod;
  wjStartMonth: number;
  /** Today's 'YYYY-MM' (the page already computes it, mirrors ``BalanceChart``).
   *  Drives the #2269 future-month Abweichung-chip suppression. Optional so callers
   *  (and tests) that omit it keep the chip on every month — undefined ⇒ never future. */
  todayYm?: string;
  /** The scope's Ist/Prognose boundary date (#3158) — the page passes
   *  ``scopedSeries.istHorizon`` (under Alle Konten: the MIN over the accounts
   *  that carry post-anchor actuals, #3241 — the same grading the caption and the
   *  aggregate's ``isActual`` use). Drives the
   *  post-horizon Ist-bisher suppression. Same optional-prop discipline as
   *  ``todayYm``: ``undefined``/'' ⇒ never hide, so every fixture that omits it
   *  stays green. */
  istHorizon?: string;
  /** Expanded L1 group ids — controlled by the page so the fold state survives the
   *  Buchungen⇄Kategorien switch (this component unmounts on toggle). A group not in
   *  the set is collapsed, so all groups start folded by default. */
  expandedGroups: Set<string>;
  onToggleGroup: (groupId: string) => void;
  /** The single open L2 category id (one at a time), also controlled by the page. */
  openCategoryId: string | null;
  onToggleCategory: (categoryId: string) => void;
  /** Click a transaction line → open the transaction form modal (Buchungen-style). */
  onTransactionClick?: (txId: string) => void;
  /** Click the "Unsortiert" row → deep-link into the Buchungen ledger to categorise the
   *  uncategorised bookings (#2347). The page wires this to ``handleViewChange('buchungen')``,
   *  mirroring the header ⚠ chip. Omitted ⇒ the row still renders but is inert. */
  onShowUnsorted?: () => void;
}) {
  // The anchor month for all window math: the selected month itself, or the WJ's
  // start month — any month inside the WJ yields the same ``wjWindowOf`` window.
  const anchorYm = periodAnchorYm(period, wjStartMonth);
  const yearScope = period.kind === 'wj';
  // #3158: a selected month FULLY past the Ist horizon has no actuals by
  // construction — its Ist is structurally 0 — yet #2269 below keys on TODAY's
  // month and so misses it whenever the bank data lags the calendar (the filed
  // WF case: ym=2026-07, today 2026-08, horizon 30.06.). The horizon-month gate
  // hides the Ist FIGURES and labels too (hero "Ist bisher", per-row Ist halves,
  // the subhead's Ist), not only the chips. Same ``!yearScope`` guard: a WJ
  // column legitimately spans post-horizon months.
  //
  // The actuals guard (codex r1 P2): under Alle Konten ``istHorizon`` is a MIN
  // across accounts, so a month past it can still hold REAL payed rows
  // from a further-along account — those must render as the partial-but-real
  // Ist they are (exactly like the horizon's own mixed month), not vanish.
  // "Structurally zero" therefore means post-horizon AND no payed record in
  // the month; the single-account case is unaffected (no bookings can exist
  // past its own horizon).
  const monthHasActuals = useMemo(
    () => records.some((r) => r.record_role === 'payed' && recordMonth(r) === anchorYm),
    [records, anchorYm],
  );
  const postHorizon =
    !yearScope &&
    istHorizon !== undefined &&
    isFullyPostHorizon(anchorYm, istHorizon) &&
    !monthHasActuals;
  // #2269: a strictly-future selected MONTH has no Ist by construction (the month is
  // entirely "Prognose ab …"), so ``saldoVariance(plan, 0)`` fires a meaningless
  // full-magnitude Abweichung chip. Suppress it on the hero + every group/category row.
  // Gated on ``!yearScope`` — the WJ column legitimately spans future months and keeps
  // its chip. In month scope ``anchorYm === period.ym``, so ``anchorYm > todayYm`` is
  // the "strictly future" test ('YYYY-MM' strings compare lexicographically). ``todayYm``
  // undefined ⇒ never future ⇒ chip unchanged (keeps existing chip tests green).
  //
  // Kept ORed with #3158's gate rather than subsumed: a non-empty horizon is
  // capped at today (computeBalanceSeries), so every #2269 month is also
  // post-horizon THERE — but on the ''-horizon shape the new arm is inert and
  // this one still has its own case.
  const hideVariance =
    (!yearScope && todayYm !== undefined && anchorYm > todayYm) || postHorizon;
  // Memoized: the accordion state lives on the page, so every fold/unfold
  // re-renders this component — without the memo each click would re-group
  // EVERY scoped record (O(N) at bank-statement scale; review r1).
  const groups = useMemo(
    () => rollupByCategory(records, anchorYm, wjStartMonth),
    [records, anchorYm, wjStartMonth],
  );

  // Grand saldo across every group (income +, expense −) for the selected period —
  // the period's net planned/actual liquidity change, shown as the HERO at the top
  // of the feed (the budget's "bottom line", surfaced first). ``signedCat`` nets
  // both-direction categories from their records (codex #2114).
  const saldo = (field: 'planMonth' | 'istMonth' | 'planYear' | 'istYear'): number =>
    groups.reduce((s, g) => s + g.categories.reduce((cs, c) => cs + signedCat(c, field, anchorYm, wjStartMonth), 0), 0);
  // #2347: the crosscutting-cash net (uncategorised + vat) the budget lens drops. Fold it
  // into the Ist hero so "Saldo des Zeitraums" == the signed net of ALL cash records ==
  // the balance chart's Δ (it was ~76 % short on WF). Plan stays budget-only — the
  // uncategorised bucket is unplannable, so it does not move the forecast.
  const unsorted = useMemo(
    () => unsortedRollup(records, anchorYm, wjStartMonth),
    [records, anchorYm, wjStartMonth],
  );
  const unsortedIst = yearScope ? unsorted.istYear : unsorted.istMonth;
  // The ROW's figure is the still-uncategorised net only (#2679) — the same population
  // its count/gross caption describes. The hero folds `unsortedIst` (all crosscutting
  // cash) so the saldo still ties to the Δ-balance; printing that on a row captioned
  // "N Buchungen noch nicht zugeordnet" would report an unpaired `transfer_internal`
  // leg as uncategorised money.
  const unsortedRowIst = yearScope ? unsorted.unsortedIstYear : unsorted.unsortedIstMonth;
  const unsortedCount = yearScope ? unsorted.countYear : unsorted.countMonth;
  const unsortedGross = yearScope ? unsorted.grossYear : unsorted.grossMonth;
  const unsortedActive = yearScope ? unsorted.hasActivityYear : unsorted.hasActivityMonth;
  const saldoPlan = saldo(yearScope ? 'planYear' : 'planMonth');
  const saldoIst = saldo(yearScope ? 'istYear' : 'istMonth') + unsortedIst;
  // The hero's "Kein Plan" signal is derived from the RECORDS, not the rendered
  // ``groups`` (#2270): ``rollupByCategory`` drops plan-only categories AND whole
  // empty groups from the tree, so a plan hidden in a dropped node would make
  // ``groups.some(...)`` falsely report "Kein Plan". ``periodHasPlan`` scans the
  // period's planned records directly (filter-independent, crosscutting excluded), so
  // a netted-to-zero saldoPlan with real plans present still keeps its variance chip;
  // only a TRUE absence of planned records anywhere flips the hero to "Kein Plan".
  const planPresence = useMemo(
    () => periodHasPlan(records, anchorYm, wjStartMonth),
    [records, anchorYm, wjStartMonth],
  );
  const saldoHasPlan = yearScope ? planPresence.year : planPresence.month;

  // Only truly empty when there is neither a budget category NOR any crosscutting cash:
  // an all-``unsorted`` period has no budget groups but must still surface its net (#2347),
  // else the omitted 76 % would fall back to "no activity" while the balance chart moves.
  if (groups.length === 0 && !unsortedActive) {
    return (
      <div className="liquiditaetsplanung-catfeed__empty">Keine Kategorien mit Aktivität in diesem Zeitraum.</div>
    );
  }

  return (
    <div className="liquiditaetsplanung-catfeed">
      {/* Period-saldo HERO (#2114): the period's net liquidity change per window
          column, "Ist bisher" as the hero figure, at the TOP of the feed so the most
          important number is seen first. The label + sub deliberately disambiguate
          this period net from the page hero "Kontostand" (account balance) — #1712. */}
      <div className="liquiditaetsplanung-catfeed__saldo">
        <span className="liquiditaetsplanung-catfeed__saldo-heading">
          <span className="liquiditaetsplanung-catfeed__saldo-label">Saldo des Zeitraums</span>
          <span className="liquiditaetsplanung-catfeed__saldo-explainer">
            Einnahmen − Ausgaben in diesem Zeitraum. Veränderung der Liquidität, nicht der Kontostand.
          </span>
        </span>
        <span className="liquiditaetsplanung-catfeed__group-rollup">
          <SaldoCell
            plan={saldoPlan}
            ist={saldoIst}
            hideVariance={hideVariance}
            hideIst={postHorizon}
            hasPlan={saldoHasPlan}
          />
        </span>
      </div>
      {/* Plan/Ist sub-labels for the single period column — rendered inside the feed
          (the page-level head strip carries only the period name, #2114/#2254). Purely
          visual: each cell's chip carries the accessible direction phrase itself.
          #3158: post-horizon the Ist LABEL empties but keeps its 6.25rem slot, so
          the Plan label stays aligned over the Plan figures. */}
      <div className="liquiditaetsplanung-catfeed__subhead" aria-hidden="true">
        <span className="liquiditaetsplanung-catfeed__subhead-col">
          <span className="liquiditaetsplanung-catfeed__subhead-plan">Plan</span>
          <span className="liquiditaetsplanung-catfeed__subhead-ist">{postHorizon ? null : 'Ist'}</span>
        </span>
      </div>
      {groups.map((group) => {
        const isCollapsed = !expandedGroups.has(group.id);
        return (
          <section
            key={group.id}
            className={`liquiditaetsplanung-catfeed__group${
              isCollapsed ? '' : ' liquiditaetsplanung-catfeed__group--open'
            }`}
          >
            <GroupHeader
              group={group}
              anchorYm={anchorYm}
              wjStartMonth={wjStartMonth}
              yearScope={yearScope}
              hideVariance={hideVariance}
              hideIst={postHorizon}
              collapsed={isCollapsed}
              onToggle={() => onToggleGroup(group.id)}
            />
            {!isCollapsed && (
              <div className="liquiditaetsplanung-catfeed__categories">
                {group.categories.map((cat) => (
                  <CategoryRow
                    key={cat.id}
                    cat={cat}
                    anchorYm={anchorYm}
                    wjStartMonth={wjStartMonth}
                    yearScope={yearScope}
                    hideVariance={hideVariance}
                    hideIst={postHorizon}
                    isOpen={openCategoryId === cat.id}
                    onToggle={() => onToggleCategory(cat.id)}
                    onTransactionClick={onTransactionClick}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
      {/* The "Unsortiert" catch-all (#2347), after the budget groups — hidden when nothing
          is uncategorised (count 0). Its net is already folded into the hero above. */}
      {unsortedCount > 0 && (
        <UnsortedRow ist={unsortedRowIst} count={unsortedCount} gross={unsortedGross} onShow={onShowUnsorted} />
      )}
    </div>
  );
}
